import type { Router, RouteRecordRaw } from 'vue-router';
import type { WithPageMeta, WithSpaceMeta } from '#layers/spaces/types/space';
import { createRouterMatcher } from 'vue-router';
import { SpacePageRouteMarker, SpaceRouteMarker } from '#layers/spaces/constants/route-markers';

export function isSpaceRoute<T extends object>(route: T): route is WithSpaceMeta<T> {
	return 'meta' in route && !!route.meta && typeof route.meta === 'object' && SpaceRouteMarker in route.meta;
}

export function isSpacePageRoute<T extends object>(route: T): route is WithPageMeta<T> {
	return 'meta' in route && !!route.meta && typeof route.meta === 'object' && SpacePageRouteMarker in route.meta;
}

type WorkspaceRouteRouter = Pick<Router, 'addRoute' | 'getRoutes' | 'removeRoute'>;

interface ReplaceWorkspaceSpaceRoutesOptions {
	router: WorkspaceRouteRouter;
	routes: readonly RouteRecordRaw[];
}

/** Removes every currently registered route marked as workspace-owned, including live replacements. */
export function clearWorkspaceSpaceRoutes(router: WorkspaceRouteRouter) {
	for (const route of router.getRoutes().filter(isSpaceRoute)) {
		if (!route.name) continue;

		try {
			router.removeRoute(route.name);
		}
		catch (error) {
			console.error('[workspace-routes] Failed to remove a workspace route', error);
		}
	}
}

/** Replaces the workspace route set and removes any partial publication if registration fails. */
export function replaceWorkspaceSpaceRoutes({ router, routes }: ReplaceWorkspaceSpaceRoutesOptions) {
	clearWorkspaceSpaceRoutes(router);

	try {
		for (const route of routes) router.addRoute('workspace-base', route);
	}
	catch (error) {
		clearWorkspaceSpaceRoutes(router);
		throw error;
	}
}

/** Parses the complete candidate route tree before the runtime enters the synchronous publication phase. */
export function validateWorkspaceSpaceRoutes(routes: readonly RouteRecordRaw[]) {
	createRouterMatcher([{
		name: 'workspace-base',
		path: '/:workspace',
		component: {},
		children: [...routes],
	}], {});
}
